import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { HelpCircle, Play, RotateCcw, BookOpen, CheckCircle2 } from 'lucide-react';
import { useTour } from '@/contexts/TourContext';
import { Badge } from '@/components/ui/badge';

export function TourHelpButton() {
  const { tourState, startTour, steps } = useTour();
  const [isOpen, setIsOpen] = useState(false);

  const completionPercentage = tourState.isCompleted 
    ? 100 
    : Math.round((tourState.currentStep / steps.length) * 100);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton tooltip="Help & Tour" className="relative font-normal text-teal-600 hover:text-teal-600 [&>svg]:h-4 [&>svg]:w-4">
          <HelpCircle className="h-4 w-4 shrink-0" />
          <span>Help &amp; Tour</span>
          {!tourState.isCompleted && tourState.currentStep > 0 && (
            <span className="absolute left-4 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
              {tourState.currentStep}
            </span>
          )}
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Product Tour</span>
          {tourState.isCompleted && (
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Completed
            </Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {!tourState.isCompleted && tourState.currentStep > 0 && (
          <>
            <div className="px-2 py-2">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium">{completionPercentage}%</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Step {tourState.currentStep + 1} of {steps.length}
              </p>
            </div>
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem onClick={startTour} className="gap-2">
          {tourState.isCompleted || tourState.currentStep === 0 ? (
            <>
              <Play className="h-4 w-4" />
              Start Tour
            </>
          ) : (
            <>
              <RotateCcw className="h-4 w-4" />
              Resume Tour
            </>
          )}
        </DropdownMenuItem>

        {tourState.currentStep > 0 && (
          <DropdownMenuItem 
            onClick={() => {
              startTour();
              setIsOpen(false);
            }} 
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Restart from Beginning
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        
        <DropdownMenuItem className="gap-2" disabled>
          <BookOpen className="h-4 w-4" />
          View Documentation
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
